import { add, scale } from "vector";
import transformPt from "../utils/transform-pt.js";
import ngon from "../utils/ngon.js";
import { sagittaArc } from "../utils/sagitta-arc.js";
import { sweep } from "../utils/sweep.js";
import { fillet } from "../utils/fillet.js";

function circle() {
  return {
    lines: [
      [
        { x: 0, y: -7, bulge: 1 },
        { x: 0, y: 7, bulge: 1 },
        { x: 0, y: -7, bulge: 1 },
      ],
    ],
    outline: [
      { x: 0, y: -8, bulge: 1 },
      { x: 0, y: 8, bulge: 1 },
      { x: 0, y: -8, bulge: 1 },
    ],
  };
}

function square() {
  return {
    lines: [
      [
        { x: -7, y: -7 },
        { x: 7, y: -7 },
        { x: 7, y: 7 },
        { x: -7, y: 7 },
        { x: -7, y: -7 },
      ],
    ],
    outline: [
      { x: -8, y: -8 },
      { x: 8, y: -8 },
      { x: 8, y: 8 },
      { x: -8, y: 8 },
    ],
  };
}

function triangle() {
  const fillet = 1;
  return {
    lines: [
      [
        { x: 0, y: -4 },
        { x: 5, y: -4, fillet },
        { x: 0, y: 7, fillet },
        { x: -5, y: -4, fillet },
        { x: 0, y: -4 },
      ],
    ],
    outline: [
      { x: 0, y: -5 },
      { x: 6.55, y: -5, fillet },
      { x: 0, y: 9.42, fillet },
      { x: -6.55, y: -5, fillet },
      { x: 0, y: -5 },
    ],
  };
}

function hexagon() {
  return {
    lines: [
      [
        { x: -7, y: 0 },
        { x: -3.5, y: -6 },
        { x: 3.5, y: -6 },
        { x: 7, y: 0 },
        { x: 3.5, y: 6 },
        { x: -3.5, y: 6 },
        { x: -7, y: 0 },
      ],
    ],
    outline: [
      { x: -8.15, y: 0 },
      { x: -4.07, y: -7 },
      { x: 4.07, y: -7 },
      { x: 8.15, y: 0 },
      { x: 4.07, y: 7 },
      { x: -4.07, y: 7 },
      { x: -8.15, y: 0 },
    ],
  };
}

function pentagon() {
  return {
    lines: [
      [
        { x: -7, y: 2.27 },
        { x: -4.33, y: -5.95 },
        { x: 4.33, y: -5.95 },
        { x: 7, y: 2.27 },
        { x: 0, y: 7.36 },
        { x: -7, y: 2 },
      ],
    ],
    outline: [
      { x: -8.18, y: 2.66 },
      { x: -5.05, y: -6.95 },
      { x: 5.05, y: -6.95 },
      { x: 8.18, y: 2.66 },
      { x: 0, y: 8.6 },
      { x: -8.18, y: 2.66 },
    ],
  };
}

function diamond() {
  return {
    lines: [
      [
        { x: 0, y: -7 },
        { x: 5, y: 0 },
        { x: 0, y: 7 },
        { x: -5, y: 0 },
        { x: 0, y: -7 },
      ],
    ],
    outline: [
      { x: 0, y: -8.72 },
      { x: 6.23, y: 0 },
      { x: 0, y: 8.72 },
      { x: -6.23, y: 0 },
      { x: 0, y: -8.72 },
    ],
  };
}

function anchor() {
  return {
    lines: [
      [
        { x: 0, y: -7 },
        { x: 5, y: 0 },
        { x: 0, y: 7 },
        { x: -5, y: 0 },
        { x: 0, y: -7 },
      ],
      [
        { x: -5, y: 0 },
        { x: 5, y: 0 },
      ],
      [
        { x: 0, y: 7 },
        { x: 0, y: -7 },
      ],
    ],
    fill: [
      [
        { x: -5, y: 0 },
        { x: 0, y: 0 },
        { x: 0, y: 7 },
      ],
      [
        { x: 0, y: 0 },
        { x: 0, y: -7 },
        { x: 5, y: 0 },
      ],
    ],
    background: null,
    outline: [
      { x: 0, y: -8.72 },
      { x: 6.23, y: 0 },
      { x: 0, y: 8.72 },
      { x: -6.23, y: 0 },
    ],
  };
}

function note() {
  const bulge = 0.414213562373095;
  return {
    lines: [
      [
        { x: -5, y: -7, bulge },
        { x: 5, y: -7 },
        { x: 6.5, y: -5.5, bulge },
        { x: 6.5, y: 5.5 },
        { x: 5, y: 7, bulge },
        { x: -5, y: 7 },
        { x: -6.5, y: 5.5, bulge },
        { x: -6.5, y: -5.5 },
        { x: -5, y: -7, bulge },
      ],
      [
        { x: -4, y: 4.5 },
        { x: 4, y: 4.5 },
      ],
      [
        { x: -4, y: 1.5 },
        { x: 4, y: 1.5 },
      ],
      [
        { x: -4, y: -1.5 },
        { x: 4, y: -1.5 },
      ],
      [
        { x: -4, y: -4.5 },
        { x: -0.5, y: -4.5 },
      ],
    ],
    background: [
      [
        { x: -5, y: -7, bulge },
        { x: 5, y: -7 },
        { x: 6.5, y: -5.5, bulge },
        { x: 6.5, y: 5.5 },
        { x: 5, y: 7, bulge },
        { x: -5, y: 7 },
        { x: -6.5, y: 5.5, bulge },
        { x: -6.5, y: -5.5 },
        { x: -5, y: -7, bulge },
      ],
    ],
    outline: [
      { x: -5, y: -8, bulge },
      { x: 5, y: -8 },
      { x: 7.5, y: -5.5, bulge },
      { x: 7.5, y: 5.5 },
      { x: 5, y: 8, bulge },
      { x: -5, y: 8 },
      { x: -7.5, y: 5.5, bulge },
      { x: -7.5, y: -5.5 },
      { x: -5, y: -8, bulge },
    ],
  };
}

function splice() {
  return {
    lines: [
      [
        { x: -7, y: -2 },
        { x: 7, y: -2 },
      ],
      [
        { x: -7, y: -3 },
        { x: 7, y: -3 },
      ],
      [
        { x: -7, y: 2 },
        { x: 7, y: 2 },
      ],
      [
        { x: -7, y: 3 },
        { x: 7, y: 3 },
      ],
    ],
    background: null,
    outline: [
      { x: -8, y: -4 },
      { x: 8, y: -4 },
      { x: 8, y: 4 },
      { x: -8, y: 4 },
    ],
  };
}

function splice_circle() {
  return {
    lines: [
      [
        { x: -7, y: -2 },
        { x: 7, y: -2 },
      ],
      [
        { x: -7, y: -3 },
        { x: 7, y: -3 },
      ],
      [
        { x: -7, y: 2 },
        { x: 7, y: 2 },
      ],
      [
        { x: -7, y: 3 },
        { x: 7, y: 3 },
      ],
      [
        { x: 0, y: -2, bulge: 1 },
        { x: 0, y: 2, bulge: 1 },
        { x: 0, y: -2, bulge: 1 },
      ],
    ],
    fill: [
      [
        { x: 0, y: -2, bulge: 1 },
        { x: 0, y: 2, bulge: 1 },
        { x: 0, y: -2, bulge: 1 },
      ],
    ],
    background: null,
    outline: [
      { x: -8, y: -4 },
      { x: 8, y: -4 },
      { x: 8, y: 4 },
      { x: -8, y: 4 },
    ],
  };
}

function checkbox() {
  return {
    lines: [
      [
        { x: -7, y: -7 },
        { x: 7, y: -7 },
        { x: 7, y: 7 },
        { x: -7, y: 7 },
        { x: -7, y: -7 },
      ],
    ],
    outline: [
      { x: -7, y: -7 },
      { x: 7, y: -7 },
      { x: 7, y: 7 },
      { x: -7, y: 7 },
      { x: -7, y: -7 },
    ],
  };
}

function checked_checkbox() {
  return {
    lines: [
      [
        { x: -7, y: -7 },
        { x: 7, y: -7 },
        { x: 7, y: 7 },
        { x: -7, y: 7 },
        { x: -7, y: -7 },
      ],
    ],
    fill: [
      [
        { x: -2.58, y: 0.25 },
        { x: -0.77, y: -1.4 },
        { x: 2.81, y: 2.75 },
        { x: 3.85, y: 1.91 },
        { x: -0.56, y: -3.27 },
        { x: -3.42, y: -0.79 },
      ],
    ],
    outline: [
      { x: -7, y: -7 },
      { x: 7, y: -7 },
      { x: 7, y: 7 },
      { x: -7, y: 7 },
      { x: -7, y: -7 },
    ],
  };
}

const icons = {
  note,
  square,
  rectangle: square,
  circle,
  triangle,
  hexagon,
  diamond,
  pentagon,
  anchor,
  splice,
  splice_circle,
  checkbox,
  checked_checkbox,
};

function drawPath(ctx, vertices) {
  const [start, ...rest] = vertices;
  ctx.beginPath();
  ctx.moveTo(start.x, start.y);
  rest.forEach((v, i) => {
    const next = vertices[i + 2];
    if (v.fillet && next) {
      const { a, b, saf, invalid } = fillet(vertices[i], v, next, v.fillet);

      if (invalid) {
        ctx.lineTo(v.x, v.y);
      } else {
        ctx.lineTo(a.x, a.y);
        ctx.arcTo(b.x, b.y, v.fillet, "0", saf);
      }
    } else if (v.bulge) {
      const a = vertices[i];
      const { r, sa, ea, ccw } = sagittaArc(a, v, v.bulge);
      const s = sweep(sa, ea, ccw);
      const laf = s > Math.PI ? "1" : "0";
      const sf = ccw ? "1" : "0";
      ctx.arcTo(v.x, v.y, r, laf, sf);
    } else {
      ctx.lineTo(v.x, v.y);
    }
  });
}

function drawIcon(
  ctx,
  icon,
  x,
  y,
  annoScale,
  color = "black",
  backgroundColor,
  highlighted = false,
  highlightColor = "blue",
  highlightedLineWidth = 2,
) {
  const ic = icons[icon]();
  const col =
    ic.background === null && backgroundColor ? backgroundColor : color;
  if (backgroundColor && ic.background !== null) {
    const bg = ic.background || ic.lines;
    const fills = bg.map((pface) =>
      pface.map((v) => ({
        x: v.x * annoScale + x,
        y: v.y * annoScale + y,
        fillet: v.fillet ? v.fillet * annoScale : null,
        bulge: v.bulge,
      })),
    );
    fills.forEach((pface) => {
      drawPath(ctx, pface);
      ctx.style("fill", backgroundColor);
      ctx.fill();
    });
  }

  if (ic.fill) {
    const fills = ic.fill.map((pface) =>
      pface.map((v) => ({
        x: v.x * annoScale + x,
        y: v.y * annoScale + y,
        fillet: v.fillet ? v.fillet * annoScale : null,
        bulge: v.bulge,
      })),
    );
    fills.forEach((pface) => {
      drawPath(ctx, pface);
      ctx.style("fill", col);
      ctx.fill();
    });
  }

  const plines = ic.lines.map((pline) =>
    pline.map((v) => ({
      x: v.x * annoScale + x,
      y: v.y * annoScale + y,
      fillet: v.fillet ? v.fillet * annoScale : null,
      bulge: v.bulge,
    })),
  );
  plines.forEach((pline) => {
    drawPath(ctx, pline);
    ctx.style("stroke", col);
    ctx.style("lineWidth", 1 * annoScale);
    ctx.stroke();
  });

  if (highlighted && ic.outline && ic.outline.length) {
    const outline = ic.outline.map((v) => ({
      x: v.x * annoScale + x,
      y: v.y * annoScale + y,
      fillet: v.fillet ? v.fillet * annoScale : null,
      bulge: v.bulge,
    }));
    drawPath(ctx, outline);
    ctx.closePath();
    ctx.style("fill", "none");
    ctx.style("stroke", highlightColor);
    ctx.style("lineWidth", highlightedLineWidth * annoScale);
    ctx.stroke();
  }
}

class Mark {
  constructor(
    center,
    label,
    {
      shape = "circle",
      offset = { x: 0, y: 0 },
      highlighted = false,
      icon = null,
      annotative = true,
      scale: ascale = 1,
      icon_only = false,
      show_checkbox = false,
      checked = false,
    } = {}, // options
  ) {
    this.center = center;
    this.label = label;
    this.shape = shape;
    this.icon = icon;
    this.offset = offset;
    this.type = "mark";
    this.highlighted = highlighted;
    this.annotative = annotative;
    this.scale = ascale;
    this.icon_only = icon_only;
    this.show_checkbox = show_checkbox;
    this.checked = checked;
  }

  render(options) {
    const ctx = options.ctx;
    const annoScale = this.annotative
      ? options.annoScale * this.scale
      : this.scale;
    const fontSize = options.fontSize * annoScale;
    const ctr = add(this.center, scale(this.offset, annoScale));
    ctx.style("fontSize", options.fontSize);
    ctx.style("font", "Menlo");
    ctx.style("textAlign", "center");
    ctx.style("annoScale", annoScale);

    if (this.icon_only) {
      const icon = this.icon || this.shape;
      drawIcon(
        ctx,
        icon,
        ctr.x,
        ctr.y,
        annoScale * 1.5,
        options.textColor,
        options.fill,
        this.highlighted,
        options.highlightColor,
        options.highlightedLineWidth,
      );
    } else {
      const mt = this.label ? ctx.measureText(this.label) : ctx.measureText("");
      let tw = mt.width * annoScale;
      if (this.icon) tw += fontSize * 1.5;
      if (this.show_checkbox) tw += fontSize * 1.5;
      const tha =
        ((mt.actualBoundingBoxAscent - mt.actualBoundingBoxDescent) / 2) *
        annoScale;

      // Draw text container
      if (this.shape === "circle") {
        if (tw > fontSize * 1.8) {
          const segLn = tw - fontSize;

          ctx.beginPath();
          ctx.moveTo(ctr.x + segLn / 2, ctr.y - fontSize);
          ctx.arcTo(ctr.x + segLn / 2, ctr.y + fontSize, fontSize, "1", "1");
          ctx.lineTo(ctr.x - segLn / 2, ctr.y + fontSize);
          ctx.arcTo(ctr.x - segLn / 2, ctr.y - fontSize, fontSize, "1", "1");
          ctx.closePath();

          if (options.fill) {
            ctx.style("fill", options.fill);
            ctx.fill();
          }

          if (options.stroke && options.lineWidth) {
            ctx.style("stroke", options.stroke);
            ctx.style("lineWidth", options.lineWidth * annoScale);
            ctx.stroke();
          }

          let shift = 0;
          if (this.icon && icons[this.icon]) {
            shift += 1;
            const ext = Math.max(tw / 2 + fontSize / 2, fontSize);
            const iconX = ctr.x - ext + fontSize;
            const iconY = ctr.y;
            drawIcon(
              ctx,
              this.icon,
              iconX,
              iconY,
              annoScale,
              options.textColor,
            );
          }

          if (this.show_checkbox) {
            const ext = Math.max(tw / 2 + fontSize / 2, fontSize);
            const iconX = ctr.x - ext + fontSize + shift * fontSize * 1.5;
            const iconY = ctr.y;
            drawIcon(
              ctx,
              this.checked ? "checked_checkbox" : "checkbox",
              iconX,
              iconY,
              annoScale,
              options.textColor,
            );
          }

          if (this.highlighted) {
            const o =
              options.lineWidth * annoScale * (1 + options.highlightOffset);

            ctx.beginPath();
            ctx.moveTo(ctr.x + segLn / 2, ctr.y - fontSize - o);
            ctx.arcTo(
              ctr.x + segLn / 2,
              ctr.y + fontSize + o,
              fontSize + o,
              "1",
              "1",
            );
            ctx.lineTo(ctr.x - segLn / 2, ctr.y + fontSize + o);
            ctx.arcTo(
              ctr.x - segLn / 2,
              ctr.y - fontSize - o,
              fontSize + o,
              "1",
              "1",
            );
            ctx.closePath();

            ctx.style("fill", "none");
            ctx.style("stroke", options.highlightColor);
            ctx.style("lineWidth", options.highlightedLineWidth * annoScale);
            ctx.stroke();
          }
        } else {
          ctx.circle(ctr.x, ctr.y, fontSize);

          if (options.fill) {
            ctx.style("fill", options.fill);
            ctx.fill();
          }

          if (options.stroke && options.lineWidth) {
            ctx.style("stroke", options.stroke);
            ctx.style("lineWidth", options.lineWidth * annoScale);
            ctx.stroke();
          }

          if (this.highlighted) {
            const o =
              options.lineWidth * annoScale * (1 + options.highlightOffset);

            ctx.circle(ctr.x, ctr.y, fontSize + o);

            ctx.style("fill", "none");
            ctx.style("stroke", options.highlightColor);
            ctx.style("lineWidth", options.highlightedLineWidth * annoScale);
            ctx.stroke();
          }
        }
      } else if (this.shape === "rectangle") {
        const ext = Math.max(tw / 2 + fontSize / 2, fontSize);

        ctx.beginPath();
        ctx.moveTo(ctr.x - ext, ctr.y - fontSize);
        ctx.lineTo(ctr.x + ext, ctr.y - fontSize);
        ctx.lineTo(ctr.x + ext, ctr.y + fontSize);
        ctx.lineTo(ctr.x - ext, ctr.y + fontSize);
        ctx.closePath();

        if (options.fill) {
          ctx.style("fill", options.fill);
          ctx.fill();
        }

        if (options.stroke && options.lineWidth) {
          ctx.style("stroke", options.stroke);
          ctx.style("lineWidth", options.lineWidth * annoScale);
          ctx.stroke();
        }

        let shift = 0;
        if (this.icon && icons[this.icon]) {
          shift += 1;
          const iconX = ctr.x - ext + fontSize;
          const iconY = ctr.y;
          drawIcon(ctx, this.icon, iconX, iconY, annoScale, options.textColor);
        }

        if (this.show_checkbox) {
          const iconX = ctr.x - ext + fontSize + shift * fontSize * 1.5;
          const iconY = ctr.y;
          drawIcon(
            ctx,
            this.checked ? "checked_checkbox" : "checkbox",
            iconX,
            iconY,
            annoScale,
            options.textColor,
          );
        }

        if (this.highlighted) {
          const o =
            options.lineWidth * annoScale * (1 + options.highlightOffset);

          ctx.beginPath();
          ctx.moveTo(ctr.x - ext - o, ctr.y - fontSize - o);
          ctx.lineTo(ctr.x + ext + o, ctr.y - fontSize - o);
          ctx.lineTo(ctr.x + ext + o, ctr.y + fontSize + o);
          ctx.lineTo(ctr.x - ext - o, ctr.y + fontSize + o);
          ctx.closePath();

          ctx.style("fill", "none");
          ctx.style("stroke", options.highlightColor);
          ctx.style("lineWidth", options.highlightedLineWidth * annoScale);
          ctx.stroke();
        }
      } else if (this.shape === "hexagon") {
        const vertices = ngon(ctr.x, ctr.y, fontSize, 6, Math.PI / 6);

        if (tw > fontSize * 1.8) {
          const inc = (tw - fontSize) / 2;

          vertices[0] = add(vertices[0], { x: -inc, y: 0 });
          vertices[1] = add(vertices[1], { x: -inc, y: 0 });
          vertices[2] = add(vertices[2], { x: -inc, y: 0 });
          vertices[3] = add(vertices[3], { x: inc, y: 0 });
          vertices[4] = add(vertices[4], { x: inc, y: 0 });
          vertices[5] = add(vertices[5], { x: inc, y: 0 });
        }

        const [start, ...rest] = vertices;
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        rest.forEach((v) => {
          ctx.lineTo(v.x, v.y);
        });
        ctx.closePath();

        if (options.fill) {
          ctx.style("fill", options.fill);
          ctx.fill();
        }

        if (options.stroke && options.lineWidth) {
          ctx.style("stroke", options.stroke);
          ctx.style("lineWidth", options.lineWidth * annoScale);
          ctx.stroke();
        }

        let shift = 0;
        if (this.icon && icons[this.icon]) {
          shift += 1;
          const ext = Math.max(tw / 2 + fontSize / 2, fontSize);
          const iconX = ctr.x - ext + fontSize;
          const iconY = ctr.y;
          drawIcon(ctx, this.icon, iconX, iconY, annoScale, options.textColor);
        }

        if (this.show_checkbox) {
          const ext = Math.max(tw / 2 + fontSize / 2, fontSize);
          const iconX = ctr.x - ext + fontSize + shift * fontSize * 1.5;
          const iconY = ctr.y;
          drawIcon(
            ctx,
            this.checked ? "checked_checkbox" : "checkbox",
            iconX,
            iconY,
            annoScale,
            options.textColor,
          );
        }

        if (this.highlighted) {
          const o =
            options.lineWidth * annoScale * (1 + options.highlightOffset);

          const oVertices = ngon(ctr.x, ctr.y, fontSize + o, 6, Math.PI / 6);
          if (tw > fontSize * 1.8) {
            const inc = (tw - fontSize) / 2;

            oVertices[0] = add(oVertices[0], { x: -inc, y: 0 });
            oVertices[1] = add(oVertices[1], { x: -inc, y: 0 });
            oVertices[2] = add(oVertices[2], { x: -inc, y: 0 });
            oVertices[3] = add(oVertices[3], { x: inc, y: 0 });
            oVertices[4] = add(oVertices[4], { x: inc, y: 0 });
            oVertices[5] = add(oVertices[5], { x: inc, y: 0 });
          }

          const [oStart, ...oRest] = oVertices;

          ctx.beginPath();
          ctx.moveTo(oStart.x, oStart.y);
          oRest.forEach((v) => {
            ctx.lineTo(v.x, v.y);
          });
          ctx.closePath();

          ctx.style("fill", "none");
          ctx.style("stroke", options.highlightColor);
          ctx.style("lineWidth", options.highlightedLineWidth * annoScale);
          ctx.stroke();
        }
      } else if (this.shape === "pentagon") {
        const vertices = ngon(ctr.x, ctr.y, fontSize, 5);
        const [start, ...rest] = vertices;
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        rest.forEach((v) => {
          ctx.lineTo(v.x, v.y);
        });
        ctx.closePath();

        if (options.fill) {
          ctx.style("fill", options.fill);
          ctx.fill();
        }

        if (options.stroke && options.lineWidth) {
          ctx.style("stroke", options.stroke);
          ctx.style("lineWidth", options.lineWidth * annoScale);
          ctx.stroke();
        }
      }

      // Draw text
      ctx.style("fill", options.textColor);
      ctx.style("fontSize", options.fontSize);

      let shift = 0;
      if (this.icon) shift += fontSize * 0.75;
      if (this.show_checkbox) shift += fontSize * 0.75;

      ctx.text(this.label || "", ctr.x + shift, ctr.y - tha);
    }
  }

  renderHitbox(options) {
    this.render(options);
    const annoScale = options.annoScale;
    const ctr = add(this.center, scale(this.offset, annoScale));

    return { pt: ctr };
  }

  get bbox() {
    return {
      xmin: this.center.x,
      xmax: this.center.x,
      ymin: this.center.y,
      ymax: this.center.y,
    };
  }

  transform(matrix) {
    const ctr = transformPt(this.center, matrix);
    return new Mark(ctr, this.label, {
      shape: this.shape,
      offset: this.offset,
      highlighted: this.highlighted,
      icon: this.icon,
    });
  }
}

export default Mark;
